/**
 * Conversion & engagement tracking utility
 * Sends events to Google Analytics (gtag) when available + cookie consent granted
 */

import { hasAnalyticsConsent } from '@/components/CookieBanner'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
    $crisp?: unknown[]
    CRISP_WEBSITE_ID?: string
  }
}

type ConversionEvent =
  | 'signup_started'
  | 'signup_step_completed'
  | 'signup_completed'
  | 'plan_selected'
  | 'checkout_started'
  | 'checkout_completed'
  | 'login_completed'
  | 'cta_clicked'
  | 'offline_mutation_created'
  | 'offline_conflict_detected'
  | 'offline_conflict_resolved_keep_mine'
  | 'offline_conflict_resolved_use_theirs'
  | 'offline_sync_success'
  | 'offline_sync_dead_letter'
  | 'offline_prefetch_completed'

type EngagementEvent =
  | 'page_view'
  | 'feature_explored'
  | 'exit_intent_shown'
  | 'exit_intent_submitted'
  | 'exit_intent_dismissed'
  | 'social_login_clicked'
  | 'faq_opened'
  | 'pricing_toggle'

interface EventParams {
  step?: number
  plan?: string
  billing?: string
  source?: string
  value?: number
  page?: string
  feature?: string
  provider?: string
  [key: string]: unknown
}

function sendEvent(event: string, category: string, params?: EventParams) {
  if (!hasAnalyticsConsent()) return
  if (typeof window.gtag !== 'function') return
  try {
    window.gtag('event', event, { event_category: category, ...params })
  } catch {
    // Silent fail — analytics should never break the app
  }
}

export function trackConversion(event: ConversionEvent, params?: EventParams) {
  sendEvent(event, 'conversion', params)
}

export function trackEngagement(event: EngagementEvent, params?: EventParams) {
  sendEvent(event, 'engagement', params)
}

/** Track page view — call on SPA navigation */
export function trackPageView(path: string, title?: string) {
  if (!hasAnalyticsConsent()) return
  if (typeof window.gtag !== 'function') return
  try {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
    })
  } catch {
    // Silent fail
  }
}
